// LoginForm.tsx
import type { FormEvent } from 'react';
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import InputGroup from 'react-bootstrap/InputGroup';
import { Alert } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

type PasswordResetValues = { username: string };

type PasswordResetFormProps = {
  onSubmit?: (values: PasswordResetValues) => Promise<void> | void;
  submitLabel?: string;
  errorMessage?: string;
};

export const PasswordResetForm = ({
  onSubmit,
  submitLabel = 'Send',
  errorMessage,
}: PasswordResetFormProps) => {
  const [username, setUsername] = useState('');
  const [touched, setTouched] = useState({ username: false });
  const [submitting, setSubmitting] = useState(false);

  // Simple, reliable email check (good enough for inline UX)
  const emailIsValid = username.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);

  const formIsValid = emailIsValid;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched({ username: true });
    if (!formIsValid) return;

    try {
      setSubmitting(true);
      await onSubmit?.({ username });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      {/* Username / Email */}
      <Form.Group className="mb-3">
        <Form.Label>Username</Form.Label>

        <Form.Control
          type="email"
          placeholder="you@example.com"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, username: true }))}
          isValid={touched.username && emailIsValid}
          isInvalid={touched.username && !emailIsValid}
          autoComplete="username"
          required
        />
        <Form.Control.Feedback type="invalid">
          Please enter a valid email address.
        </Form.Control.Feedback>
      </Form.Group>

      <Button
        type="submit"
        variant="primary"
        disabled={!formIsValid || submitting}
        className="w-100"
      >
        {submitting && (
          <>
            <FontAwesomeIcon icon={faSpinner} spin />{' '}
          </>
        )}
        {submitLabel}
      </Button>
    </Form>
  );
};
